import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { InputField, TextareaField } from "../../components/ui/Inputs";
import SelectOptions from "../../components/ui/SelectOptions";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../services/redux/hooks";
import {
  resetSubmitState,
  setSubmitLoading,
  setSubmitSuccess,
} from "../../services/redux/slices/supportTicketSlice";
import service from "../../services/api/service";
import { ROUTES } from "../../routes/routerConfig";
import { DashboardOutlinedButton, PrimaryButton } from "../../components/ui/Button";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import Card from "../../components/ui/Card";
import TicketReflectionNotice from "../../components/common/TicketReflectionNotice";
import postsServices from "../../services/api/service/postsService";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import { borderRadius } from "@mui/system";
import { decryptObject, encrypt, encryptObject } from "../../utils/crypto";
import { ApiTicketItem } from "./support.types";
import { formatDateToShortMonth } from "../../utils/dateFormatUtils";
import { validateDocumentFile } from "../../utils/imageValidation";
import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import { useAuth } from "../../hooks/useAuth";
import { qrcFlowTypeSelector, qrcLanSelector, qrcLanOptionsSelector } from "../../services/redux/slices/authSlice";
import { useQrcIdentifiers } from "./qrcIdentifiers";

// ─── Constants ────────────────────────────────────────────────────────────────

const ACCEPTED_MIME = ["application/pdf", "image/png", "image/jpeg"];
const ACCEPTED_EXT = ".pdf, .jpg, .jpeg, .png";
// Quoted in the dropzone hint and the error below, so the number lives in one place.
const MAX_TOTAL_SIZE_MB = 10; // combined across all files
const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;
const DESC_MAX = 500;
const DESC_MIN = 20;

// Ticket statuses that mean a request for the selected account/application is already open
// (see API_STATUS_MAP in support.types): 6 = Pending Information, 4 = Resolved.
const EXISTING_TICKET_STATUS_CODES = [6, 4,2,7,10,11];

// ─── Schema ───────────────────────────────────────────────────────────────────

interface RaiseRequestForm {
  loanAccountNumber: string;
  description: string;
  communicationContact: string;
}

// Shown instead of the Loan Account/Application Number field for a non-borrower
// pre-login visitor (see HelpCenter's "No, I don't" path), who has no loan account.
// Each concern opens its own follow-up fields below, so the two wordings are named here
// rather than written out again at every site that has to recognise which one is selected.
const CONCERN_COMMUNICATION = "I am receiving Email/SMS/Calls for a loan I have not availed";
const CONCERN_CREDIT_REPORT = "My Credit Report reflects a loan I have not availed";

const CONCERN_OPTIONS = [
  { value: CONCERN_COMMUNICATION, label: CONCERN_COMMUNICATION, type: "concern" },
  { value: CONCERN_CREDIT_REPORT, label: CONCERN_CREDIT_REPORT, type: "concern" },
];

// The documents a credit-report concern has to carry, replacing the free-form upload box.
// create-ticket takes every file under one repeated `attachments` key and has no field per
// document, so this order is the only thing telling support which upload is which — keep it
// in step with what they expect to read, and append them in exactly this order on submit.
const CREDIT_REPORT_DOCS = [
  { key: "creditReport", label: "Cibil / Credit Report", required: true },
  { key: "maskedPan", label: "Masked PAN", required: true },
  { key: "maskedAadhaar", label: "Masked Aadhar", required: true },
  { key: "cancelledCheque", label: "Cancelled Cheque", required: false },
] as const;

type CreditReportDocKey = (typeof CREDIT_REPORT_DOCS)[number]["key"];

// A customer is chased on one or the other, so the field takes either rather than forcing a
// format on someone who only has the one they were contacted on. The mobile rule mirrors the
// rest of the portal: ten digits starting 6-9, with an optional +91 / 0 in front.
const MOBILE_PATTERN = /^(?:\+?91[-\s]?|0)?[6-9]\d{9}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const schema = yup.object({
  loanAccountNumber: yup.string().required("This field is required"),
  // Asked for only on the "receiving Email/SMS/Calls" concern, where the number or address
  // being contacted is the substance of the complaint. Every other path — borrower, post-login,
  // the credit-report concern — never renders the field, so it has to stay optional there or
  // Submit would sit disabled with nothing on screen to explain why. The default keeps it a
  // plain string for the resolver rather than an optional one.
  communicationContact: yup
    .string()
    .default("")
    .transform((value) => (typeof value === "string" ? value.trim() : value))
    .when("loanAccountNumber", {
      is: CONCERN_COMMUNICATION,
      then: (field) =>
        field
          .required("Mobile number or email ID is required")
          .test(
            "mobile-or-email",
            "Enter a valid 10-digit mobile number or email ID",
            (value) => MOBILE_PATTERN.test(value ?? "") || EMAIL_PATTERN.test(value ?? "")
          ),
    }),
  description: yup
    .string()
    // Trim before the length rules, otherwise whitespace counts towards the minimum and a
    // description of nothing but spaces submits. Also keeps padding out of what we send.
    .transform((value) => (typeof value === "string" ? value.trim() : value))
    .required("Description is required")
    .min(DESC_MIN, `Minimum ${DESC_MIN} characters required`)
    .max(DESC_MAX, `Maximum ${DESC_MAX} characters allowed`),
});

// ─── File validation ──────────────────────────────────────────────────────────

interface FileError {
  name: string;
  reason: string;
}

async function validateFiles(
  incoming: File[],
  existing: File[]
): Promise<{ valid: File[]; errors: FileError[] }> {
  const errors: FileError[] = [];
  const valid: File[] = [];

  const existingTotalSize = existing.reduce((sum, f) => sum + f.size, 0);
  let accumulatedSize = existingTotalSize;

  for (const file of incoming) {
    if (!ACCEPTED_MIME.includes(file.type)) {
      errors.push({ name: file.name, reason: "Only PDF, JPG, PNG allowed" });
    } else {
      const contentCheck = await validateDocumentFile(file);
      if (!contentCheck.isValid) {
        errors.push({ name: file.name, reason: "File content does not match its type. Only real PDF, JPG, PNG allowed" });
      } else if (accumulatedSize + file.size > MAX_TOTAL_SIZE_BYTES) {
        errors.push({
          name: file.name,
          reason: `Total upload size exceeds ${MAX_TOTAL_SIZE_MB} MB limit`,
        });
      } else {
        valid.push(file);
        accumulatedSize += file.size;
      }
    }
  }

  return { valid, errors };
}

// ─── Named document field ─────────────────────────────────────────────────────
// One compact field per document the credit-report concern asks for, sized to sit four
// across so the whole set reads as a single row of the form rather than a stack that pushes
// everything else past the fold. A field holds one file and picking again replaces it, which
// is what someone correcting a wrong upload expects and what keeps the uploads lined up with
// the labels support reads them back under.

interface DocumentSlotProps {
  label: string;
  required: boolean;
  file?: File;
  error?: string;
  onPick: (files: FileList | null) => void;
  onRemove: () => void;
  onPreview: (file: File) => void;
}

const DocumentSlot: React.FC<DocumentSlotProps> = ({
  label, required, file, error, onPick, onRemove, onPreview,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    // Neither the label nor the box is text to take away, and the box is a click target, so an
    // ordinary click or slight drag would otherwise leave the label highlighted and unreadable
    // under the browser's selection colour.
    <Box sx={{ userSelect: "none" }}>
      <label className="text-lable">
        {label}
        {required && <Box component="span" color="error.main"> *</Box>}
      </label>

      <Box
        // The whole box opens the picker while it is empty; once a file is in, it goes inert so
        // its two icon buttons are what respond.
        onClick={file ? undefined : () => inputRef.current?.click()}
        role={file ? undefined : "button"}
        tabIndex={file ? undefined : 0}
        onKeyDown={
          file
            ? undefined
            : (e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  inputRef.current?.click();
                }
              }
        }
        sx={{
          // Matches the height and radius of the form's other inputs, so the row of four sits
          // on the same rhythm as the Description box above it.
          mt: 0.5,
          height: 46,
          px: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          border: `1px solid ${error ? "#e53935" : file ? "rgba(0,179,181,0.55)" : "#C8CCD4"}`,
          borderRadius: "8px",
          bgcolor: file ? "rgba(0,179,181,0.06)" : "#fff",
          cursor: file ? "default" : "pointer",
          transition: "border-color 0.2s, background-color 0.2s",
          "&:hover": file ? undefined : { borderColor: "#00B3B5", bgcolor: "rgba(0,179,181,0.03)" },
          "&:focus-visible": { outline: "2px solid #00B3B5", outlineOffset: "-2px" },
        }}
      >
        {file ? (
          <>
            <CheckIcon sx={{ fontSize: 16, color: "#00B3B5", flexShrink: 0 }} />
            {/* noWrap because the column is only a quarter of the form wide — the full name is
                still there on hover, and the two icons must keep their room. */}
            <Typography
              noWrap
              title={file.name}
              sx={{ flex: 1, minWidth: 0, fontSize: "0.8rem", color: "text.primary" }}
            >
              {file.name}
            </Typography>
            <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onPreview(file)} aria-label={`View ${label}`}>
              <VisibilityOutlinedIcon sx={{ fontSize: 17, color: "#00B3B5" }} />
            </IconButton>
            <IconButton size="small" sx={{ p: 0.25 }} onClick={onRemove} aria-label={`Remove ${label}`}>
              <DeleteOutlineIcon sx={{ fontSize: 17, color: "#e53935" }} />
            </IconButton>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              width: "100%",
              color: "#00B3B5",
            }}
          >
            <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>Upload</Typography>
          </Box>
        )}

        {/* Single-file: a field stands for one document and a second would have no label to sit
            under. stopPropagation because the programmatic .click() above bubbles back to the
            box, which would otherwise reopen the picker. Reset after each pick so choosing the
            same file twice still fires onChange. */}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXT}
          hidden
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => { onPick(e.target.files); e.target.value = ""; }}
        />
      </Box>

      {error && (
        <FormHelperText error sx={{ mt: 0.25, fontSize: "0.7rem", lineHeight: 1.4 }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

interface PreviewFileInfo {
  name: string;
  type: string;
  url: string;
}

const RaiseARequest: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { submitStatus, ticketId } = useAppSelector((state) => state.supportTicket);

  const [attachments, setAttachments] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<FileError[]>([]);
  // The credit-report concern's four named uploads, kept apart from `attachments` so each one
  // stays addressable by slot. They are flattened back into a plain list on submit.
  const [creditReportDocs, setCreditReportDocs] = useState<Partial<Record<CreditReportDocKey, File>>>({});
  const [creditReportDocErrors, setCreditReportDocErrors] = useState<Partial<Record<CreditReportDocKey, string>>>({});
  const [dragOver, setDragOver] = useState(false);
  const [submittedAt, setSubmittedAt] = useState("");
  
  // Document preview state variables
  const [previewFile, setPreviewFile] = useState<PreviewFileInfo | null>(null);
  const [openPreview, setOpenPreview] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));

  const [hasExistingTicket, setHasExistingTicket] = useState(false);
  const { isAuthenticated } = useAuth();
  const qrcFlowType = useSelector(qrcFlowTypeSelector);
  const qrcLan = useSelector(qrcLanSelector);
  const qrcLanOptions: string[] = useSelector(qrcLanOptionsSelector) ?? [];
  // A logged-in session always wins over stale QRC state left in this tab by an earlier pre-login
  // journey — otherwise the pre-login treatment (and its LAN/Concern field swap) leaks onto
  // post-login screens.
  const isNonBorrowerConcern = qrcFlowType === "nonBorrower" && !isAuthenticated;
  const isBorrowerLanInput = qrcFlowType === "borrower" && !isAuthenticated;
  const isQrcFlow = !!qrcFlowType && !isAuthenticated;
  const borrowerLanOptions = qrcLanOptions.map((lan) => ({ value: lan, label: lan, type: "accountNumber" }));
  // Post-login the same qrc/user-verification look-up the pre-login journey runs, so an
  // application that has a CRM id but no LAN yet is selectable here too (empty pre-login, where
  // qrcLanOptions already holds it).
  const verifiedLanOptions = useQrcIdentifiers().map((lan) => ({
    value: lan,
    label: lan,
    type: "accountNumber",
  }));

  // Landing on this page is always a fresh start, so clear any submit status left behind. The
  // slice is persisted to sessionStorage, so refreshing mid-submit brought back "loading" while
  // the request itself died with the page — leaving the Backdrop below spinning with nothing
  // left to switch it off. Running only on mount, this hides that stuck loader on refresh.
  useEffect(() => {
    dispatch(resetSubmitState());
  }, [dispatch]);

  // Mirrors ViewTickets' loanOptions, deduped by value. Post-login lists exactly what
  // qrc/user-verification returns, matching the pre-login journey for the same mobile (and the
  // View Tickets filter). A pre-login QRC visitor sees only what their own verification
  // returned — never the persisted option slice, which can still hold the loan accounts of
  // whoever was logged in on this tab earlier.
  // qrc/user-verification returns both buckets — disbursed loans and in-flight applications
  // (see extractLanList) — so it is the whole list. user-application/fetch used to stand behind
  // it, but it only ever repeated the application half of the same data, and was requested on
  // every visit to fill a gap that no longer exists.
  const lanDropdownOptions = (
    isQrcFlow ? borrowerLanOptions : verifiedLanOptions
  ).filter(
    (opt, index, all) => all.findIndex((o) => o.value === opt.value) === index
  );
  const [selectedLoan, setSelectedLoan] = useState({ value: '', label: '', type: '' });
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm<RaiseRequestForm>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      loanAccountNumber: "",
      description: "",
      communicationContact: "",
    },
  });

  // Keyed off qrcLan alone rather than isBorrowerLanInput: a QRC LAN only exists for a QRC
  // session (logging in clears it, see setToken in authSlice), and gating on the flag meant the
  // pre-fill silently dropped in the same cases that emptied the dropdown above.
  useEffect(() => {
    if (qrcLan) {
      setValue("loanAccountNumber", qrcLan, { shouldValidate: true });
      setSelectedLoan({ value: qrcLan, label: qrcLan, type: "accountNumber" });
    }
  }, [qrcLan, setValue]);

  const watchedDesc = watch("description") ?? "";
  // Trimmed, to match what the schema validates (see the description rule above).
  const descLen = watchedDesc.trim().length;

  // ─── Concern-specific fields ("No, I don't" flow only) ─────────────────────
  // The chosen concern lives in the loanAccountNumber field, which this flow swaps to the
  // Concern dropdown (see dropdownOptions below). Each concern opens its own follow-up: the
  // contact someone is being chased on, or the documents a credit-bureau dispute needs.
  const selectedConcern = watch("loanAccountNumber") ?? "";
  const isCommunicationConcern = isNonBorrowerConcern && selectedConcern === CONCERN_COMMUNICATION;
  const isCreditReportConcern = isNonBorrowerConcern && selectedConcern === CONCERN_CREDIT_REPORT;

  // A credit-report concern gathers everything through its four named slots, so the free-form
  // box is hidden and its "at least one file" rule lifted there. Every other path — including
  // the other concern — keeps that box exactly as it was.
  const requiresGenericDocument = isNonBorrowerConcern && !isCreditReportConcern;
  const missingRequiredDocs = CREDIT_REPORT_DOCS.filter(
    (doc) => doc.required && !creditReportDocs[doc.key]
  );
  // The named slots flattened into submit order. Also what the size check counts: the 10 MB
  // ceiling is a total for the ticket, so a file added through either box has to be measured
  // against everything the other one already holds.
  const namedDocFiles = CREDIT_REPORT_DOCS
    .map((doc) => creditReportDocs[doc.key])
    .filter((file): file is File => !!file);

  // ─── Existing-ticket check ───────────────────────────────────────────────────
  // On selecting a loan account / application number, look up its tickets. If one is
  // already open (Pending Information / Resolved), warn the user with a link to the listing.
  const checkExistingTickets = useCallback(async (lanValue: string) => {
    setHasExistingTicket(false);
    if (!lanValue) return;
    try {
      const encryptedData = encryptObject({ lan: [lanValue] });
      const res = await service.support.getTicketListing( { data: encryptedData });
      const rawData = res?.data?.data; // sometimes the encrypted string directly, sometimes { data, pagination }
      const encryptedTickets = typeof rawData === "string" ? rawData : rawData?.data;
      const apiTickets: ApiTicketItem[] = decryptObject(encryptedTickets) ?? [];
      setHasExistingTicket(
        apiTickets.some((t) => EXISTING_TICKET_STATUS_CODES.includes(t.status))
      );
    } catch {
      setHasExistingTicket(false);
    }
  }, []);

  // ─── File handlers ─────────────────────────────────────────────────────────

  const addFiles = useCallback(
    async (incoming: FileList | null) => {
      if (!incoming) return;
      // Measured against the named slots too — both boxes can be on screen at once and they
      // share one 10 MB budget.
      const { valid, errors: errs } = await validateFiles(Array.from(incoming), [
        ...attachments,
        ...namedDocFiles,
      ]);
      setFileErrors(errs);
      setAttachments((prev) => [...prev, ...valid]);
    },
    [attachments, creditReportDocs]
  );

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    await addFiles(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setFileErrors([]);
  };

  // One slot at a time, but measured against whatever the other three already hold — the 10 MB
  // ceiling is a total across the ticket, which is exactly what validateFiles' second argument
  // is for. Only the picked slot's own error is touched, so a rejection here doesn't wipe the
  // message sitting under a different slot.
  const pickCreditReportDoc = useCallback(
    async (key: CreditReportDocKey, incoming: FileList | null) => {
      const file = incoming?.[0];
      if (!file) return;
      const alreadyHeld = [
        ...CREDIT_REPORT_DOCS
          .filter((doc) => doc.key !== key)
          .map((doc) => creditReportDocs[doc.key])
          .filter((f): f is File => !!f),
        ...attachments,
      ];
      const { valid, errors: errs } = await validateFiles([file], alreadyHeld);
      setCreditReportDocErrors((prev) => ({ ...prev, [key]: errs[0]?.reason ?? "" }));
      if (valid[0]) setCreditReportDocs((prev) => ({ ...prev, [key]: valid[0] }));
    },
    [creditReportDocs, attachments]
  );

  const removeCreditReportDoc = (key: CreditReportDocKey) => {
    setCreditReportDocs((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setCreditReportDocErrors((prev) => ({ ...prev, [key]: "" }));
  };

  // ─── Preview Modal handlers ────────────────────────────────────────────────

  const handleOpenPreview = (file: File) => {
    const fileUrl = URL.createObjectURL(file);
    setPreviewFile({
      name: file.name,
      type: file.type,
      url: fileUrl,
    });
    setOpenPreview(true);
  };

  const handleClosePreview = () => {
    if (previewFile?.url) {
      URL.revokeObjectURL(previewFile.url);
    }
    setOpenPreview(false);
    setPreviewFile(null);
  };

  // ─── Submit ────────────────────────────────────────────────────────────────

  const onSubmit: SubmitHandler<RaiseRequestForm> = async (data) => {
    dispatch(setSubmitLoading());
    const formData = new FormData();
    // The QRC flows ("Yes, I do" / "No, I don't") are always pre-login — even when a
    // main-account session happens to be active — so they must send "prelogin".
    const loginType = isQrcFlow ? "prelogin" : isAuthenticated ? "postlogin" : "prelogin";

    if (isNonBorrowerConcern) {
      
      // New user ("No, I don't"): there is no loan account to key the ticket against, so `key` is
      // the literal "N/A". Their chosen concern goes in complaintDetails and the free text they
      // typed in complaintReason — that is the mapping the API expects here, and no
      // breachedTicketId is sent. The concern is held in the loanAccountNumber form field,
      // which this flow swaps to the Concern dropdown (see dropdownOptions below).
      //formData.append("key", DOMPurify.sanitize(encrypt("N/A")));
      formData.append("SelectConcern", DOMPurify.sanitize(encrypt(data.loanAccountNumber)));
      // The number or address being chased is part of the complaint itself, and create-ticket
      // has no field of its own for it — every other key here carries a document — so it goes
      // on the end of what the customer typed, on its own line so support can pick it out.
      const complaintReason = isCommunicationConcern
        ? `${data.description}\n\n | Receiving communication on: ${data.communicationContact}`
        : data.description;
      formData.append("complaintReason", DOMPurify.sanitize(encrypt(complaintReason)));
    } else {
      formData.append("key", DOMPurify.sanitize(encrypt(data.loanAccountNumber)));
      formData.append("description", DOMPurify.sanitize(encrypt(data.description)));
    }
    formData.append("loginType", DOMPurify.sanitize(encrypt(loginType)));
    // The named documents lead, in CREDIT_REPORT_DOCS order, because that order is the only
    // thing identifying each one — anything the customer added through the free-form box below
    // them follows. The named slots are only read on the concern that shows them: switching
    // concern leaves the other one's files in state, and sending those would attach a credit
    // report to a communication ticket.
    const filesToSend = [
      ...(isCreditReportConcern ? namedDocFiles : []),
      ...attachments,
    ];
    filesToSend.forEach((file) => formData.append("attachments", file));
    try {
      postsServices.addPost("api/v1/qrc/create-ticket", formData)
        .then((response) => {
          try {
            const encryptedPayload = response.data?.data;
            const decrypted = decryptObject(encryptedPayload) as { id: number; submissionDate: string };
            const formattedDate = formatDateToShortMonth(decrypted?.submissionDate ?? new Date().toISOString());
            setSubmittedAt(formattedDate);
            dispatch(setSubmitSuccess(decrypted?.id ? String(decrypted.id) : "-"));
          } catch (e) {
            setSubmittedAt(formatDateToShortMonth(new Date().toISOString()));
            dispatch(setSubmitSuccess("-"));
          }
        })
        .catch((error: any) => {
          dispatch(setSubmitSuccess(""));
          // Show the error message returned by the API.
          dispatch(
            setStatusPopup({
              status: "error",
              message: error?.response?.data?.message,
            })
          );
        });


    } catch {
      dispatch(setSubmitSuccess(`TKT-${Date.now()}`));
    }
  };

  const handleSuccessClose = () => {
    dispatch(resetSubmitState());
    reset();
    setAttachments([]);
    setCreditReportDocs({});
    setCreditReportDocErrors({});
    setSubmittedAt("");
    navigate(ROUTES.ContactUs.viewTickets.locationPath);
  };

  const handleCancel = () => {
    dispatch(resetSubmitState());
    reset();
    setAttachments([]);
    setCreditReportDocs({});
    setCreditReportDocErrors({});
    navigate(-1);
  };

  const isLoading = submitStatus === "loading";

  // ─── Breadcrumb items ──────────────────────────────────────────────────────

  const breadcrumbItems = [
    { label: "Dashboard", path: "/" },
    { label: "Contact Us" },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Backdrop open={isLoading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* {!isXsOrSm && !isQrcFlow && (
        <Box sx={{ padding: "0px 0px 18px 0px" }}>
          <Breadcrumbs items={breadcrumbItems} />
        </Box>
      )} */}

      <Grid container>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            sx={{
              color: "#168794",
              fontSize: "15px",
              fontWeight: "600",
              marginBottom: isQrcFlow ? "12px" : "20px",
            }}
          >
            {isQrcFlow ? "Raise New Ticket" : "Raise a Service Request"}
          </Typography>
        </Grid>
      </Grid>
      {/* Centered form card — in the QRC hub the form already sits on the shell card, so drop
          this card's shadow and padding rather than nesting a card inside a card. */}
      <Card elevated={!isQrcFlow} padding={isQrcFlow ? "0px" : undefined}>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate

        >
          {/* ── Loan Account / Application Number (borrower + default) or Concern (non-borrower) ── */}
          <Box sx={{ mb: 1 }}>
            <Controller
              name="loanAccountNumber"
              control={control}
              render={({ field }) => {
                const dropdownOptions = isNonBorrowerConcern ? CONCERN_OPTIONS : lanDropdownOptions;
                return (
                  <SelectOptions
                    options={dropdownOptions}
                    label={isNonBorrowerConcern ? "Select Concern" : "Loan Account/Application Number"}
                    value={field.value}
                    isMandatory
                    onChange={(val) => {
                      const stringVal = String(val);

                      // 1. Update React Hook Form
                      field.onChange(stringVal);

                      if (!isNonBorrowerConcern) {
                        // 2. Find the selected option object from the active options list
                        const selectedOption = dropdownOptions?.find(option => String(option.value) === stringVal);
                        // 3. Store the value, label, and type in your local state
                        setSelectedLoan({
                          value: stringVal,
                          label: selectedOption ? selectedOption.label : '',
                          type: selectedOption ? selectedOption.type : '' // 'accountNumber' or 'applicationNumber'
                        });
                        // 4. Warn if a ticket is already open for this account/application
                        checkExistingTickets(stringVal);
                      }
                    }}
                    error={!!errors.loanAccountNumber}
                    helperText={errors.loanAccountNumber?.message}
                  />
                );
              }}
            />

            {hasExistingTicket && (
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: "#FCF6E3",
                }}
              >
                <InfoOutlinedIcon sx={{ color: "#EAB308", fontSize: 20, mt: "1px", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.875rem", color: "#3A3A3A", lineHeight: 1.5 }}>
                 
                  <Box
                    component="span"
                    onClick={() => navigate(ROUTES.ContactUs.viewTickets.locationPath)}
                    sx={{
                      color: "#00B3B5",
                      fontWeight: 600,
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    Click here
                  </Box>{" "}
                 to view existing service requests before creating a new one.
                </Typography>
              </Box>
            )}
          </Box>

          {/* ── Where the unwanted communication is arriving (that concern only) ── */}
          {isCommunicationConcern && (
            <Box sx={{ mb: 1 }}>
              <label className="text-lable">
                Mobile Number / Email ID receiving the communication{" "}
                <Box component="span" color="error.main">*</Box>
              </label>
              <Controller
                name="communicationContact"
                control={control}
                render={({ field }) => (
                  <InputField
                    {...field}
                    fullWidth
                    placeholder="Enter the mobile number or email ID"
                    error={!!errors.communicationContact}
                    helperText={errors.communicationContact?.message}
                    borderRadius="8px"
                  />
                )}
              />
            </Box>
          )}

          {/* ── Description ── */}
          <Box>
            <label className="text-lable">
              Description{" "}
              <Box component="span" color="error.main">*</Box>
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextareaField
                  {...field}
                  fullWidth
                  multiline
                  // Inside the QRC hub card the 5-row box was twice the height of the design's
                  // and left the form looking hollow.
                  rows={isQrcFlow ? 5 : 5}
                  placeholder="Please describe your request or query here..."
                  error={!!errors.description}
                  inputProps={{ maxLength: DESC_MAX }}
                  borderRadius="8px"
                  padding="12px 14px"
                />
              )}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
              <FormHelperText error={!!errors.description} sx={{ m: 0 }}>
                {errors.description?.message}
              </FormHelperText>
              <Typography
                variant="caption"
                color={descLen < DESC_MIN || descLen > DESC_MAX ? "error" : "text.secondary"}
              >
                {descLen} / {DESC_MAX} Characters (Min {DESC_MIN} required)
              </Typography>
            </Box>
          </Box>

          {/* ── Named documents a credit-report dispute needs ── */}
          {/* Asked for by name rather than left to the free-form box below, which a customer has
              to guess the contents of. Four across on a wide form so the set reads as one row of
              it; two up, then one, as the form narrows. Each carries its own label and asterisk,
              so the group needs no heading of its own. */}
          {isCreditReportConcern && (
            <Box sx={{ mb: 2 }}>
              <Grid container spacing={1.5}>
                {CREDIT_REPORT_DOCS.map((doc) => (
                  <Grid item xs={12} sm={6} md={3} key={doc.key}>
                    <DocumentSlot
                      label={doc.label}
                      required={doc.required}
                      file={creditReportDocs[doc.key]}
                      error={creditReportDocErrors[doc.key]}
                      onPick={(files) => pickCreditReportDoc(doc.key, files)}
                      onRemove={() => removeCreditReportDoc(doc.key)}
                      onPreview={handleOpenPreview}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* ── Supporting Documents (free-form — shown on every flow) ── */}
          {/* Named "Additional Documents" when it follows the four above, so the two don't read
              as the same section twice; on its own it stays "Supporting Documents" as always. */}
          <Box sx={{ mb: 2, userSelect: "none" }}>
            <label className="text-lable">
              {isCreditReportConcern ? "Additional Documents" : "Supporting Documents"}
              {requiresGenericDocument && <Box component="span" color="error.main"> *</Box>}
            </label>

            {/* Drop zone */}
            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${dragOver ? "#00B3B5" : "#C8CCD4"}`,
                borderRadius: 2,
                py: 1,
                px: 1,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: dragOver ? "rgba(0,179,181,0.03)" : "#fafbfc",
                transition: "border-color 0.2s, background-color 0.2s",
                "&:hover": { borderColor: "#00B3B5", bgcolor: "rgba(0,179,181,0.03)" },
              }}
            >
              <FileUploadOutlinedIcon
                sx={{ fontSize: isQrcFlow ? 36 : 36, color: "#00B3B5", mb: isQrcFlow ? 1 : 1 }}
              />
              <Typography variant="body2" fontWeight={700} color="text.primary">
                Drop File here or click to upload
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Max size: {MAX_TOTAL_SIZE_MB}MB. Supported: PDF, JPG, PNG.
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXT}
                multiple
                hidden
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              />
            </Box>

            {/* File errors */}
            {fileErrors.map((fe, i) => (
              <Alert key={i} severity="warning" sx={{ mt: 1, py: 0 }}>
                {fe.name ? `${fe.name}: ` : ""}{fe.reason}
              </Alert>
            ))}

            {/* Attached files */}
            <Collapse in={attachments.length > 0}>
              <Box sx={{ mt: 1.5 }}>
                {attachments.map((file, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      py: 1,
                      mb: 0.5,
                      borderRadius: 1.5,
                      bgcolor: "rgba(0,179,181,0.06)",
                      border: "1px solid rgba(0,179,181,0.2)",
                    }}
                  >
                    <Typography variant="body2" noWrap sx={{ maxWidth: "70%", color: "text.primary" }}>
                      {file.name}
                      <Box component="span" sx={{ ml: 1, color: "text.secondary", fontSize: "0.75rem" }}>
                        ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                      </Box>
                    </Typography>
                    
                    {/* Action controls flex layout container */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleOpenPreview(file)} aria-label="View file">
                        <VisibilityOutlinedIcon fontSize="small" sx={{ color: "#00B3B5" }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => removeFile(i)} aria-label="Remove file">
                        <DeleteOutlineIcon fontSize="small" sx={{ color: "#e53935" }} />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Collapse>

            {requiresGenericDocument && attachments.length === 0 && (
              <FormHelperText error sx={{ mt: 1 }}>
                At least one supporting document is required
              </FormHelperText>
            )}
          </Box>

          {/* ── Submit ── (left-aligned in the QRC hub, matching the form's field edge) */}
          <Box sx={{ textAlign: isQrcFlow ? "left" : "center" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={
                !isValid ||
                (requiresGenericDocument && attachments.length === 0) ||
                (isCreditReportConcern && missingRequiredDocs.length > 0)
              }
              sx={{
                width: { xs: "100%", sm: "auto" },
                px: { xs: 2, sm: isQrcFlow ? 4 : 6 },
                py: isQrcFlow ? 1 : 1.25,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                fontSize: isQrcFlow ? "0.875rem" : "1rem",
                bgcolor: "#00B3B5",
                color: "#fff",
                boxShadow: "none",
                "&:hover": { bgcolor: "#009799", boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#aaa" },
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Card>

      {/* ── Document Preview Popup Dialog ── */}
      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" noWrap sx={{ maxWidth: '85%', fontWeight: 600 }}>
            {previewFile?.name}
          </Typography>
          <IconButton onClick={handleClosePreview} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#fafbfc' }}>
          {previewFile?.type?.includes("pdf") ? (
            <iframe
              src={previewFile.url}
              width="100%"
              height="100%"
              title="PDF Preview"
              style={{ border: "none" }}
            />
          ) : previewFile?.type?.includes("image") ? (
            <img
              src={previewFile.url}
              alt="Uploaded document preview"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          ) : (
            <Typography variant="body1" color="text.secondary">
              Preview is not supported for this file format.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Success dialog ── */}
      <Dialog
        open={submitStatus === "success"}
        onClose={handleSuccessClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 3 } }}
        className="raise-request-modal"
      >
        <DialogContent sx={{ p: 0, textAlign: "center" }}>
          {/* Teal circle checkmark */}
          <Box
            sx={{
              width: 68,
              height: 68,
              borderRadius: "50%",
              bgcolor: "#00B3B5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2.5,
            }}
          >
            <CheckIcon sx={{ fontSize: 38, color: "#fff" }} />
          </Box>

          {/* Title */}
          <Typography variant="h6" className="raise-request-success-label" sx={{ mb: 1 }}>
            Request Successfully Submitted!
          </Typography>

          {/* Subtitle */}
          <Typography variant="body2" className="raise-request-sub-label" sx={{ mb: 3 }}>
            Your request has been submitted and is being processed.
          </Typography>

          {/* Info table */}
          <Box sx={{ mb: 2.5, }}>
            <div className="view-tickets-container">
              <Box className="raise-request-info-row raise-request-info-row-b-1">
                <Typography className="inside-page-row-lable">
                  Ticket Number
                </Typography>
                <Typography className="inside-page-row-value">
                  {ticketId}
                </Typography>
              </Box>
              <Box className="raise-request-info-row raise-request-info-row-b-2">
                <Typography className="inside-page-row-lable">
                  Date of Submission
                </Typography>
                <Typography className="inside-page-row-value">
                  {submittedAt}
                </Typography>
              </Box>
            </div>
          </Box>

          <TicketReflectionNotice />

          {/* Note */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, px: 0.5 }}>
            You will receive updates on your registered email and mobile number.
            You can also track the status of your request from the 'View Existing Requests' section.
          </Typography>

          {/* Action buttons */}
          <Box>
            <PrimaryButton onClick={handleSuccessClose} sx={{ mr: 1 }}>
              View Tickets
            </PrimaryButton>
            {/* Hide the dashboard button for the QRC pre-login flows ("Yes, I do" / "No, I don't"). */}
            {!isQrcFlow && (
              <DashboardOutlinedButton
                fullWidth
                onClick={() => {
                  dispatch(resetSubmitState());
                  if (!isAuthenticated) {
                    navigate(ROUTES.HelpCenter.locationPath);
                  } else if (selectedLoan?.type === 'applicationNumber' ) {
                    navigate("/application/existing-application");
                  } else {
                    navigate("/dashboard");
                  }
                }}
              >
                {isAuthenticated ? "Go to Dashboard" : "Back to Help Center"}
              </DashboardOutlinedButton>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RaiseARequest;